/**
 * The /start -> space hand-off hint.
 *
 * Provisioning ends in a DOCUMENT navigation into the new space, and the
 * space shell then needs three network reads (session, project, active
 * wizard) before it can know the wizard should cover everything. Without a
 * hint the shell paints its sidebar skeleton first - in the server HTML
 * already, then again through every client loading state - and the wizard's
 * blackroom only after; a flash the reader sees on every fresh start. This
 * marker is written right before the navigation and read synchronously by
 * every loading state on the way in, so the blackroom is the first paint.
 *
 * A COOKIE, not storage: the space route is server-rendered, so the hint has
 * to reach the server's first HTML too. It is scoped to the space's own path
 * (`/spaces/<slug>`), so the browser only presents it on that space and it
 * can never colour another space's boot. The wizard state, once loaded,
 * confirms it (rewrites the phase) or clears it (no active session), so a
 * stale marker can only cost one blackroom paint on a space whose wizard
 * already ended.
 */

/**
 * `cover`: a fresh session with nothing answered - the WELCOME cover is the
 * first slide, so the skeleton is a bare blackroom (no credits UI at all).
 * `wizard`: the session resumes mid-wizard - the skeleton may show the folded
 * header meter.
 */
export type OnboardingPendingPhase = 'cover' | 'wizard';

export const ONBOARDING_PENDING_COOKIE = 'onboarding-wizard-pending';

/**
 * Long enough for a reload mid-wizard days later to still open on the room;
 * the gate clears the cookie the moment the server reports no session, so
 * the ceiling only matters for a space never revisited.
 */
const MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

export const onboardingPendingCookiePath = (slug: string) =>
	`/spaces/${encodeURIComponent(slug)}`;

/** Server and client alike: the cookie's raw value to a phase, anything else to `null`. */
export const parseOnboardingPendingPhase = (
	value: string | null | undefined,
): OnboardingPendingPhase | null =>
	value === 'cover' || value === 'wizard' ? value : null;

const canUseCookies = () => typeof document !== 'undefined';

/**
 * The marker for the CURRENT document, read from `document.cookie` - the
 * browser has already scoped it to this space's path. Server code reads the
 * request cookie and parses it with `parseOnboardingPendingPhase` instead.
 */
export const readOnboardingPendingMarker = (): OnboardingPendingPhase | null => {
	if (!canUseCookies()) return null;
	const entry = document.cookie
		.split('; ')
		.find((pair) => pair.startsWith(`${ONBOARDING_PENDING_COOKIE}=`));
	return parseOnboardingPendingPhase(entry?.slice(ONBOARDING_PENDING_COOKIE.length + 1));
};

const cookieAttributes = (slug: string, maxAge: number) => {
	const secure = typeof location !== 'undefined' && location.protocol === 'https:';
	return `path=${onboardingPendingCookiePath(slug)}; max-age=${maxAge}; samesite=lax${secure ? '; secure' : ''}`;
};

export const writeOnboardingPendingMarker = (
	slug: string | null | undefined,
	phase: OnboardingPendingPhase,
): void => {
	if (!slug || !canUseCookies()) return;
	document.cookie = `${ONBOARDING_PENDING_COOKIE}=${phase}; ${cookieAttributes(slug, MAX_AGE_SECONDS)}`;
};

export const clearOnboardingPendingMarker = (slug: string | null | undefined): void => {
	if (!slug || !canUseCookies()) return;
	document.cookie = `${ONBOARDING_PENDING_COOKIE}=; ${cookieAttributes(slug, 0)}`;
};
